import { redeemAmount } from '@/api/user'; // Ensure this import is added
import { useProfile } from '@/components/providers/profile-provider';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
// Updated import
import { useDeletePaymentMethod } from '@/hooks/query-hooks/use-delete-payment';
import { isActionError } from '@/lib/error';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Image from 'next/image';
import React, { useState } from 'react';
import { BankForm } from '../../payment/_components/bank-form';
import { UpiForm } from '../../payment/_components/upi-form';
import PaymentCard from './bank-card';
import PaymentMethodButton from './payment-method-button';
import RedeemDialog from './redeem-amount-dialog';
interface TransferDestinationProps {
  type: 'bank' | 'upi';
  details: string;
  maskedId: string;
}
function BankIcon() {
  return <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none" className="fill-[#999CA0] group-hover:fill-white" data-sentry-element="svg" data-sentry-component="BankIcon" data-sentry-source-file="payment-method-selector-modal.tsx">
      <path d="M14.375 1.24219L2.5 7.49219V9.99219H26.25V7.49219M20 12.4922V21.2422H23.75V12.4922M2.5 27.4922H26.25V23.7422H2.5M12.5 12.4922V21.2422H16.25V12.4922M5 12.4922V21.2422H8.75V12.4922H5Z" className="fill-[#999CA0] group-hover:fill-white" data-sentry-element="path" data-sentry-source-file="payment-method-selector-modal.tsx" />
    </svg>;
}
;
export function PaymentMethodModal({
  open,
  onOpenChange,
  redeem = false
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  redeem?: boolean;
}) {
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [isRedeemOpen, setIsRedeemOpen] = useState(false);
  const [transferDestination, setTransferDestination] = useState<TransferDestinationProps | null>(null);
  const {
    paymentMethods,
    referrals
  } = useProfile();
  const {
    handleDelete
  } = useDeletePaymentMethod();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const redeemMutation = useMutation({
    mutationFn: (amount: number) => redeemAmount(amount, transferDestination?.type.toLowerCase() as 'bank' | 'upi'),
    onSuccess: data => {
      if (isActionError(data)) {
        setErrorMessage(data.error);
        return;
      }
      setIsSuccess(true);
      setErrorMessage(null);
      setTimeout(() => {
        setIsRedeemOpen(false);
        onOpenChange(false);
        setIsSuccess(false);
      }, 5000);
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || 'Redemption failed');
      console.error('Redeem failed:', error);
    }
  });
  const handleRedeem = async (amount: number) => {
    redeemMutation.mutate(amount);
    await queryClient.invalidateQueries({
      queryKey: ['withdrawals', 'referrals']
    });
  };
  const handlePaymentDelete = async (method: 'bank' | 'upi') => {
    try {
      await handleDelete(method);
    } catch (error) {
      console.error('Error deleting payment method:', error);
    }
  };
  const handlePaymentSelection = (method: TransferDestinationProps) => {
    setIsRedeemOpen(true);
    setTransferDestination(method);
  };
  return <>
      <Dialog open={open} onOpenChange={onOpenChange} data-sentry-element="Dialog" data-sentry-source-file="payment-method-selector-modal.tsx">
        <DialogContent className="mx-3" data-sentry-element="DialogContent" data-sentry-source-file="payment-method-selector-modal.tsx">
          <DialogHeader data-sentry-element="DialogHeader" data-sentry-source-file="payment-method-selector-modal.tsx">
            <DialogTitle className="text-center" data-sentry-element="DialogTitle" data-sentry-source-file="payment-method-selector-modal.tsx">
              {!selectedMethod && <span>Select payment method</span>}
            </DialogTitle>
          </DialogHeader>
          {selectedMethod === 'Bank' ? <BankForm onBack={() => setSelectedMethod(null)} onSuccessClose={() => {
          onOpenChange(false);
          setSelectedMethod(null);
        }} /> : selectedMethod === 'UPI' ? <UpiForm onBack={() => setSelectedMethod(null)} onSuccessClose={() => {
          onOpenChange(false);
          setSelectedMethod(null);
        }} /> : <div className="p-2">
                    <div className="flex flex-col gap-2">
                      {paymentMethods?.bank ? <PaymentCard title={paymentMethods.bank.bankName} identifier={paymentMethods.bank.accountNumber} isVerified onDelete={() => handlePaymentDelete('bank')} icon={<BankIcon />} onClick={() => handlePaymentSelection({
              details: paymentMethods.bank?.bankName ?? '',
              maskedId: paymentMethods.bank?.accountNumber ?? '',
              type: 'bank'
            })} /> : <PaymentMethodButton onClick={() => setSelectedMethod('Bank')} method="Bank" className="group shadow-sm hover:bg-x-accent-alpha hover:text-white" icon={<BankIcon />} />}
                      {paymentMethods?.upi ? <PaymentCard title="UPI ID" identifier={paymentMethods.upi} onDelete={() => handlePaymentDelete('upi')} isVerified icon={<Image src="/upi.svg" alt="UPI" width={30} height={30} />} onClick={() => handlePaymentSelection({
              details: paymentMethods.upi ?? '',
              maskedId: '',
              type: 'upi'
            })} /> : <PaymentMethodButton onClick={() => setSelectedMethod('UPI')} method="UPI" className="group shadow-sm hover:bg-x-accent-alpha hover:text-white" icon={<BankIcon />} />}
                    </div>
                  </div>}
        </DialogContent>
      </Dialog>
      {redeem && <RedeemDialog open={isRedeemOpen} isSuccess={isSuccess} onOpenChange={(status: boolean) => setIsRedeemOpen(status)} availableBalance={referrals?.wallet.availableBalance as number} transferDestination={transferDestination} onRedeem={handleRedeem} errorMessage={errorMessage} />}
    </>;
}
;